import {
    Alert,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import React, {useContext} from 'react'
import {Trans} from 'react-i18next'
import {ClinicalConditionState, ComplaintState, ProductTypeOptions,} from '../../../contexts/types'
import productTypeIsOneOf from '../../../lib/productTypeIsOneOf'
import {ErrorRecord} from '../../../hooks/useForm'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        alertFormLabel: {
            color: 'rgb(102, 60, 0)',
            '&.Mui-focused': {
                color: 'rgb(102, 60, 0)',
            },
        },
    })
)

type TemporaryOptionsProps = {
    data: ClinicalConditionState
    errors: ErrorRecord<ClinicalConditionState>
    complaint: ComplaintState
    handleChange: (
        fieldName: keyof ClinicalConditionState,
        value: string
    ) => void
}

const TemporaryOptions: React.FC<TemporaryOptionsProps> = ({
    data,
    errors,
    complaint,
    handleChange,
}) => {
    const classes = useStyles()
    const { t, tns } = useContext(OrgCustomizationContext)
    const productType: ProductTypeOptions = complaint.generalComplaint
        .productType as ProductTypeOptions

    return (
        <>
            {/* Temporary Conditions */}
            <Typography variant='body1' color='secondary.contrastText'>
                <Trans i18nKey={tns('cci_temporary_conditions')}>
                    -
                    <Typography component='span' sx={{ color: 'primary.main' }}>
                        -
                    </Typography>
                    -
                </Trans>
            </Typography>
            <FormControlLabel
                value='ALLERGIC_REACTION'
                control={<Radio />}
                label={t('cci_option_allergic_reaction') as string}
            />
            <FormControlLabel
                value='ASPIRATION'
                control={<Radio />}
                label={t('cci_option_aspiration') as string}
            />

            {/* Temporary Conditions - Damage of soft tissue or bone */}
            <Typography
                variant='body1'
                color='secondary.contrastText'
                sx={{ pt: 2 }}
            >
                <Trans i18nKey={tns('cci_temporary_conditions_damage')}>
                    -
                    <Typography component='span' sx={{ color: 'primary.main' }}>
                        -
                    </Typography>
                    -
                </Trans>
            </Typography>

            {productTypeIsOneOf(
                ['IMPLANT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='SINUS_PERFORATION'
                    control={<Radio />}
                    label={t('cci_option_sinus_perforation') as string}
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='BONE_FRACTURE'
                    control={<Radio />}
                    label={t('cci_option_bone_fracture') as string}
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='ABSCESS'
                    control={<Radio />}
                    label={t('cci_option_abscess') as string}
                />
            )}

            {productTypeIsOneOf(['IMPLANT'], productType) && (
                <FormControlLabel
                    value='FISTULA'
                    control={<Radio />}
                    label={t('cci_option_fistula') as string}
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='DAMAGING_OF_BLOOD_VESSELS'
                    control={<Radio />}
                    label={t('cci_option_damaging_of_blood_vessels') as string}
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'SCREW'],
                productType
            ) &&
                complaint.generalComplaint.wasPatientInvolved !== 'YES' && (
                    <FormControlLabel
                        value='HAMPERS_DIAGNOSTICS'
                        control={<Radio />}
                        label={t('cci_option_hampers_diagnostics') as string}
                    />
                )}

            {/* Applies to all product types */}
            <FormControlLabel
                value='LOCAL_INFECTION'
                control={<Radio />}
                label={t('cci_option_local_infection') as string}
            />

            {productTypeIsOneOf(['IMPLANT'], productType) && (
                <FormControlLabel
                    value='OSTEOMYELITIS'
                    control={<Radio />}
                    label={t('cci_option_osteomyelitis') as string}
                />
            )}

            {productTypeIsOneOf(['IMPLANT'], productType) && (
                <FormControlLabel
                    value='REVISION_SURGERY_IMPLANT_EXTRACTION'
                    control={<Radio />}
                    label={
                        t(
                            'cci_option_revision_surgery_implant_extraction'
                        ) as string
                    }
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'SCREW'],
                productType
            ) && (
                <FormControlLabel
                    value='REVISION_SURGERY_ASSEMBLY_ABUTMENT_IMPLANT'
                    control={<Radio />}
                    label={
                        t(
                            'cci_option_revision_surgery_assembly_abutment_implant'
                        ) as string
                    }
                />
            )}

            {/* Applies to all product types */}
            <FormControlLabel
                value='LOCAL_INFLAMMATION'
                control={<Radio />}
                label={t('cci_option_local_inflammation') as string}
            />

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='TRANSIENT_PAIN'
                    control={<Radio />}
                    label={t('cci_option_transient_pain') as string}
                />
            )}

            {data.clinicalCondition === 'TRANSIENT_PAIN' && (
                <Alert severity='warning' icon={false}>
                    <FormControl>
                        <FormLabel classes={{ root: classes.alertFormLabel }}>
                            {t('cci_has_pain_disappeared')}
                        </FormLabel>
                        <RadioGroup
                            row
                            value={data.hasPainDisappeared}
                            onChange={(e) =>
                                handleChange(
                                    'hasPainDisappeared',
                                    e.target.value
                                )
                            }
                        >
                            <FormControlLabel
                                value='YES'
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                value='NO'
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        <FormHelperText error={!!errors.hasPainDisappeared}>
                            {errors.hasPainDisappeared}
                        </FormHelperText>
                    </FormControl>
                </Alert>
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='NUMBNESS'
                    control={<Radio />}
                    label={t('cci_option_numbness') as string}
                />
            )}

            {data.clinicalCondition === 'NUMBNESS' && (
                <Alert severity='warning' icon={false}>
                    <FormControl>
                        <FormLabel classes={{ root: classes.alertFormLabel }}>
                            {t('cci_has_numbness_disappeared')}
                        </FormLabel>
                        <RadioGroup
                            row
                            value={data.hasNumbnessDisappeared}
                            onChange={(e) =>
                                handleChange(
                                    'hasNumbnessDisappeared',
                                    e.target.value
                                )
                            }
                        >
                            <FormControlLabel
                                value='YES'
                                control={<Radio />}
                                label='Yes'
                            />
                            <FormControlLabel
                                value='NO'
                                control={<Radio />}
                                label='No'
                            />
                        </RadioGroup>
                        <FormHelperText error={!!errors.hasNumbnessDisappeared}>
                            {errors.hasNumbnessDisappeared}
                        </FormHelperText>
                    </FormControl>
                </Alert>
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'SCREW', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <>
                    <FormControlLabel
                        value='PROLONGED_SURGERY'
                        control={<Radio />}
                        label={t('cci_option_prolonged_surgery') as string}
                    />

                    {data.clinicalCondition === 'PROLONGED_SURGERY' && (
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={4}
                            sx={{
                                maxWidth: '40rem',
                            }}
                            name='prolongedSurgeryExplanation'
                            error={!!errors.prolongedSurgeryExplanation}
                            helperText={errors.prolongedSurgeryExplanation}
                            value={data.prolongedSurgeryExplanation}
                            onChange={(e) =>
                                handleChange(
                                    'prolongedSurgeryExplanation',
                                    e.target.value
                                )
                            }
                        />
                    )}
                </>
            )}

            <FormControlLabel
                value='PROLONGED_TREATMENT_TIME'
                control={<Radio />}
                label={t('cci_option_prolonged_treatment_time') as string}
            />

            {data.clinicalCondition === 'PROLONGED_TREATMENT_TIME' && (
                <TextField
                    multiline
                    minRows={2}
                    maxRows={4}
                    sx={{
                        maxWidth: '40rem',
                    }}
                    name='prolongedTreatmentExplanation'
                    error={!!errors.prolongedTreatmentExplanation}
                    helperText={errors.prolongedTreatmentExplanation}
                    value={data.prolongedTreatmentExplanation}
                    onChange={(e) =>
                        handleChange(
                            'prolongedTreatmentExplanation',
                            e.target.value
                        )
                    }
                />
            )}

            {productTypeIsOneOf(
                ['IMPLANT', 'ABUTMENT', 'SCREW', 'CLINICAL_INSTRUMENT'],
                productType
            ) && (
                <FormControlLabel
                    value='SWALLOWING_MEDICAL_DEVICE'
                    control={<Radio />}
                    label={t('cci_option_swallowing_medical_device') as string}
                />
            )}
        </>
    )
}

export default TemporaryOptions
